import { AlarmClock } from 'lucide-react';
import { anzahl, datumDe, datumKompaktDe, tageUeberfaellig } from '@/lib/format';
import { istUeberfaellig } from '@/lib/ueberfaellig';

/**
 * Zeigt an, dass ein zugesagtes Abholdatum verstrichen ist (B63).
 *
 * Vorher stand das Datum nur als Zahl da — ob es schon vorbei war, musste
 * man selbst ausrechnen. Der Badge nennt beides: dass es ueberfaellig ist
 * und seit wann.
 *
 * `istEndzustand` ist PFLICHT und nicht optional (A37, #107). Der Grund
 * ist der Befund: Die Detailansicht rief diesen Badge ohne jede Pruefung
 * auf, und eine abgeholte Bestellung stand dort als ueberfaellig. Eine
 * Eigenschaft mit Vorgabewert haette denselben Fehler stillschweigend
 * ueberlebt; so erinnert der Uebersetzer jede kuenftige Aufrufstelle
 * daran, dass zur Frage zwei Angaben gehoeren.
 *
 * A57.2 (#123): DIE MAHNUNG NENNT DEN REST
 *   Bis hierher stand da nur „ueberfaellig seit 28.08." — wer es las,
 *   musste die Bestellung oeffnen, um zu erfahren, ob noch drei Titel
 *   fehlen oder einer.
 *
 *   Die REGEL, wann etwas ueberfaellig ist, bleibt dabei unangetastet:
 *   sie steht weiterhin allein in lib/ueberfaellig.ts und, fuer die
 *   Serverseite, in DASH_UEBERFAELLIG_WHERE. Der Rest haengt sich an sie
 *   an, er entscheidet nicht mit. Eine Bestellung ohne Rest ist genauso
 *   ueberfaellig wie eine mit — sie ist nur da und noch nicht abgeholt.
 *
 *   `restOffen` ist wie `istEndzustand` PFLICHT und nicht optional, aus
 *   demselben Grund: Eine Eigenschaft mit Vorgabewert haette es einer
 *   kuenftigen Aufrufstelle erlaubt, den Rest stillschweigend
 *   wegzulassen, und niemand haette gesehen, dass die Mahnung dort
 *   weniger sagt als anderswo. `null` heisst ausdruecklich „hier gibt es
 *   nichts zu melden", nicht „vergessen".
 */
export function UeberfaelligBadge({
  zugesagt,
  istEndzustand,
  restOffen
}: {
  zugesagt: string | null;
  istEndzustand: boolean;
  /**
   * Wieviele Exemplare noch fehlen, oder null. Kommt aus
   * `fortschritt.offen` und damit vom Server (A57.1) — hier wird nichts
   * nachgerechnet.
   */
  restOffen: number | null;
}) {
  const tage = tageUeberfaellig(zugesagt);
  // Die REGEL steht in istUeberfaellig(). Die null-Abfrage davor ist
  // keine zweite Regel, sondern die Einengung fuer den Uebersetzer: unten
  // rechnet anzahl(tage, …) mit einer Zahl.
  if (tage === null || !istUeberfaellig(istEndzustand, tage)) return null;

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-coral bg-coral-soft px-2 py-0.5 text-xs text-coral"
      title={
        `Zugesagt war der ${datumDe(zugesagt)} — seither ${anzahl(tage, 'Tag', 'Tage')}.` +
        (restOffen !== null && restOffen > 0
          ? ` ${anzahl(restOffen, 'Exemplar fehlt', 'Exemplare fehlen')} noch.`
          : '')
      }
    >
      <AlarmClock className="h-3 w-3" />
      überfällig seit {datumKompaktDe(zugesagt)}
      {/* A57.2: „· 2 fehlen".
          NICHT „· 2 offen", obwohl der Auftrag es so vorschlug. Auf
          derselben Karte steht schon eine Marke „3 offen" — das sind
          die Exemplare, die noch keinem Beschaffungsweg zugeordnet
          sind. Zwei Zahlen mit demselben Wort und verschiedener
          Bedeutung nebeneinander sind schlimmer als eine fehlende:
          Wer sie verwechselt, bestellt nach, was laengst unterwegs ist.
          „fehlen" sagt dasselbe und ist nicht belegt. */}
      {restOffen !== null && restOffen > 0 && (
        <span className="tabular-nums"> · {anzahl(restOffen, 'fehlt', 'fehlen')}</span>
      )}
    </span>
  );
}
